import { NextRouter } from "next/router"
import { FlightQuoteItineraryPayload } from "@/types/flight-quote-details"
import type { FlightSliceTicketing } from "@/types/flights/flight-itinerary"
import { FlightItineraryApi } from "@/types/flights/flight-itinerary-api"
import { Optional } from "@/types/util"

import {
  buildFlightSearchInputFromItinerary,
  buildFlightSheetUrlParams,
} from "@/lib/flight-utils"
import { FlightOption } from "../types/flights/flight-option"

export const FLIGHT_CENTRE_CHAT_URL = "https://direct.lc.chat/11509253/137"

export type FlightQuoteDisplayOption = Omit<FlightOption, "slice_ticketing"> & {
  slice_ticketing?: FlightSliceTicketing
}

export function buildFlightOptionsFromItinerary(
  flightItinerary: Optional<FlightItineraryApi>
): FlightOption[] {
  return (
    flightItinerary?.flight_slices?.map((slice, ix) => {
      return {
        trip_type: flightItinerary.trip_type,
        is_split_ticket: flightItinerary.properties.is_split_ticket,
        price: flightItinerary.properties.price,
        favorite: flightItinerary.favorite,
        slice_ticketing: flightItinerary.properties.slice_ticketing[ix],
        flight_slice: slice,
        vendor_data: flightItinerary.vendor_data ?? {},
        vendor: flightItinerary.vendor,
        // Additional fields needed for Sabre revise flow
        passenger_pricing: flightItinerary.properties.passenger_pricing,
        upgrades: flightItinerary.properties.upgrades,
        itinerary_id: flightItinerary.vendor_data?.itinerary_id,
        bookable: flightItinerary.properties?.bookable,
      }
    }) ?? []
  )
}

export function buildFlightOptionsFromQuoteItinerary(
  itinerary: FlightQuoteItineraryPayload
): FlightQuoteDisplayOption[] {
  const slices = itinerary.flight_slices
  const sliceTicketing = itinerary.properties.slice_ticketing

  return slices.map((slice, ix) => ({
    trip_type: itinerary.trip_type,
    is_split_ticket: itinerary.properties.is_split_ticket,
    favorite: itinerary.favorite,
    price: itinerary.properties.price,
    vendor: itinerary.vendor,
    vendor_data: {
      contract_id: itinerary.vendor_data?.contract_id,
      additional_offers_refs: undefined,
    },
    slice_ticketing: sliceTicketing?.[ix],
    flight_slice: slice,
    flight_slices: slices,
    bookable: itinerary.properties.bookable ?? false,
    flight_itinerary_id: itinerary.id ?? undefined,
    itinerary_id: itinerary.vendor_data?.itinerary_id,
  }))
}

function chatBaseUrl(flightItinerary: FlightItineraryApi): string {
  if (!flightItinerary.latest_chat_id) return "/chat"
  return `/chat/${flightItinerary.latest_chat_id}`
}

export function reviseFlightItinerary(
  flightItinerary: FlightItineraryApi,
  router: NextRouter
) {
  const sheetInput = buildFlightSearchInputFromItinerary(flightItinerary, true)
  sheetInput.revise = true
  const urlParams = buildFlightSheetUrlParams(sheetInput)
  urlParams.set("view", "flight-search")
  void router.push(
    `${chatBaseUrl(flightItinerary)}?${urlParams.toString()}`,
    undefined,
    { shallow: false }
  )
}

export function buildDepartureSearchUrl(
  flightItinerary: FlightItineraryApi,
  options?: { expired?: boolean }
): string {
  const sheetInput = buildFlightSearchInputFromItinerary(flightItinerary, false)
  const urlParams = buildFlightSheetUrlParams(sheetInput)
  urlParams.set("view", "flight-search")
  if (options?.expired) {
    urlParams.set("expired", "1")
  }
  return `${chatBaseUrl(flightItinerary)}?${urlParams.toString()}`
}

export function redirectToDepartureSearch(
  flightItinerary: FlightItineraryApi,
  router: NextRouter,
  options?: { expired?: boolean }
) {
  void router.push(
    buildDepartureSearchUrl(flightItinerary, options),
    undefined,
    {
      shallow: false,
    }
  )
}
